import { useCallback, useMemo } from 'react';
import { identity, memoizeWith } from 'ramda';
import { useModalHandlersAndOptions } from '../../useModal';
import { ModalName } from '../../../../constants/ModalName';
import { useStorageQuota } from '../useStorageQuota';
import { createHOCWithHookInside } from '../../../hoc/createHOCWithHookInside';
import { StorageQuotaUseCase } from './StorageQuotaUseCase';

const memoResultOf = <T extends Function>(fn: T) => memoizeWith(identity, fn);

export const useStorageQuotaAwareHandler = () => {
  const { storageQuotaData } = useStorageQuota();
  const uploadsRestricted = storageQuotaData?.uploadsRestricted || false;
  const { openModal } = useModalHandlersAndOptions(ModalName.StorageFull);
  const showStorageFullModal = useMemo(
    () =>
      memoResultOf((useCase: StorageQuotaUseCase): VoidFunction => {
        return () => openModal({ useCase });
      }),
    [openModal],
  );

  const storageQuotaAware = <Fn extends Function>(fn: Fn, useCase: StorageQuotaUseCase): Fn | VoidFunction => {
    if (uploadsRestricted) {
      return showStorageFullModal(useCase);
    }

    return fn;
  };

  return useCallback(storageQuotaAware, [uploadsRestricted, showStorageFullModal]);
};

/**
 * @deprecated use "useStorageQuotaAwareHandler"
 */
export const withStorageQuotaAwareHandler = createHOCWithHookInside('withStorageQuotaAwareHandler', () => ({
  storageQuotaAwareHandler: useStorageQuotaAwareHandler(),
}));
